<template>
  <!-- Modal (okno dialogowe) pojawia się jeśli isVisible = true -->
  <div v-if="isVisible" class="modal-overlay" @click.self="closeModal">
    <div class="modal">
      <header class="modal-header">
        <h2 class="margin-0">{{ title }}</h2>
        <!-- Przycisk zamykania modal'a -->
        <button class="close-button" @click="closeModal">×</button>
      </header>

      <section class="modal-body">
        <!-- Formularz zgłoszeniowy, ukrywa się po wysłaniu -->
        <form v-if="!formSent" @submit.prevent="handleSubmit">
          <div class="form-group">
            <label for="name">{{ t('name-surname') }}</label>
            <input type="text" id="name" v-model="form.name" :placeholder="t('name-surname')" required />
          </div>

          <div class="form-group">
            <label for="email">{{ t('email') }}</label>
            <input type="email" id="email" v-model="form.email" :placeholder="t('email-address')" required />
          </div>

          <div class="form-group">
            <label for="message">{{ t('message') }}</label>
            <textarea id="message" v-model="form.message" :placeholder="t('your-message')" required></textarea>
          </div>

          <div class="form-group">
            <label for="file">{{ t('attachments') }}</label>
            <!-- Pole do załączania pliku -->
            <input ref="fileInput" type="file" id="file" @change="handleFileChange" />
            <span v-if="form.file && form.file.name">
              {{ t('selected-file') }}: {{ form.file.name }}
              <button type="button" @click="removeFile" class="remove-file-button">X</button>
            </span>
            <span v-else>{{ t('no-selected-file') }}</span>
          </div>

          <!-- Przycisk wysłania formularza -->
          <button
            type="submit"
            class="submit-btn"
            :disabled="isSubmitting"
            :aria-busy="isSubmitting ? 'true' : 'false'"
          >
            <!-- <span v-if="isSubmitting" class="spinner" aria-hidden="true"></span> -->
            <span>{{ isSubmitting ? ('Wysyłanie...') : t('send-ticket') }}</span>
          </button>
        </form>

        <!-- Komunikat sukcesu po wysłaniu zgłoszenia -->
        <div v-else class="success-message center-aligned">
          <p>{{ t('ticket-sent') }}</p>
          <button @click="closeModal" class="close-success-btn">{{ t('close') }}</button>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import TicketsService from '@/services/TicketsService';
import { useNotificationStore } from '@/stores/notification.store';

export default {
  props: {
    isVisible: { type: Boolean, required: true },
    title: { type: String, default: 'Formularz zwrotny' },
  },

  data() {
    return {
      form: {
        name: '',
        email: '',
        message: '',
        file: null,
      },
      formSent: false,     // czy formularz wysłany (pokazuje widok sukcesu)
      isSubmitting: false, // czy trwa wysyłka (spinner + disable)
    };
  },

  watch: {
    // Gdy modal zostanie zamknięty przez rodzica, posprzątaj stan,
    // żeby po ponownym otwarciu można było dodać kolejne zgłoszenie.
    isVisible(newVal) {
      if (!newVal) {
        this.formSent = false;
        this.isSubmitting = false;
        this.resetForm();
      }
    },
  },

  methods: {
    // Zamknięcie modala przez rodzica
    closeModal() {
      // Przy zamykaniu wyczyść stan (również z widoku sukcesu)
      this.formSent = false;
      this.isSubmitting = false;
      this.resetForm();
      this.$emit('close');
    },

    // Submit formularza
    handleSubmit() {
      this.saveTicket();
    },

    async saveTicket() {
      if (this.isSubmitting) return; // zabezpieczenie przed podwójnym kliknięciem
      this.isSubmitting = true;

      try {
        await TicketsService.saveTicket(this.form.message, this.form.file);
        // Po sukcesie pokaż okno sukcesu i wyczyść pola
        this.formSent = true;
        this.resetForm();
      } catch (error) {
        console.error('Błąd podczas zapisywania zgłoszenia:', error);
        useNotificationStore().error(
          'Wystąpił błąd podczas zapisywania zgłoszenia. Spróbuj ponownie później.'
        );
      } finally {
        this.isSubmitting = false;
      }
    },

    // Handler na zmianę pliku
    handleFileChange(event) {
      this.form.file = event.target.files[0] || null;
    },

    // Usuwanie pliku z formularza
    removeFile() {
      this.form.file = null;
      if (this.$refs.fileInput) this.$refs.fileInput.value = '';
    },

    // Resetuje formularz do wartości początkowych
    resetForm() {
      this.form.name = '';
      this.form.email = '';
      this.form.message = '';
      this.form.file = null;
      if (this.$refs.fileInput) this.$refs.fileInput.value = '';
    },
  },
};
</script>

<style scoped>
/* Modal zgłoszenia — okno dialogowe w tym samym języku wizualnym co panele:
   nagłówek na jasnym tle, treść z odstępami, akcja główna na dole. */

.modal-overlay {
  position: fixed;
  inset: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.35);
  display: flex;
  justify-content: center;
  align-items: center;
  padding: var(--spacing-lg);
  z-index: 9999;
}

.modal {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 480px;
  max-height: calc(100vh - 2 * var(--spacing-xl));
  padding: 0;
  background-color: var(--surface-primary);
  border: 1px solid var(--border-default);
  border-radius: var(--panel-radius);
  box-shadow: var(--shadow-overlay);
  overflow: hidden;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--spacing-md);
  padding: var(--spacing-md) var(--spacing-lg);
  margin: 0;
  background: var(--surface-secondary);
  border-bottom: 1px solid var(--border-default);
}

.modal-header h2 {
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-bold);
  letter-spacing: var(--letter-spacing-caps);
  text-transform: uppercase;
  color: var(--text-primary);
}

.close-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: var(--control-height-sm);
  height: var(--control-height-sm);
  background: none;
  border: 0;
  border-radius: var(--control-radius);
  color: var(--text-secondary);
  font-size: var(--font-size-xl);
  line-height: 1;
  cursor: pointer;
}

.close-button:hover {
  background: var(--surface-hover);
  color: var(--text-primary);
}

.modal-body {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
  padding: var(--spacing-lg);
  overflow-y: auto;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
  margin: 0;
}

label {
  display: block;
  margin: 0;
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
}

input,
textarea {
  width: 100%;
}

textarea {
  min-height: 120px;
}

/* Akcja główna modala */
button.submit-btn {
  width: 100%;
  margin-top: var(--spacing-sm);
}

/* Komunikat po wysłaniu zgłoszenia */
.success-message {
  padding: var(--spacing-lg);
  background-color: var(--status-ok-soft);
  border: 1px solid var(--status-ok-border);
  border-radius: var(--control-radius);
  color: var(--status-ok);
}

.success-message p {
  margin: 0 0 var(--spacing-md);
}

.remove-file-button {
  margin-left: var(--spacing-sm);
  color: var(--status-error);
  background-color: transparent;
  border: none;
  cursor: pointer;
}

/* Nazwa wybranego pliku / informacja o braku pliku */
.form-group > span {
  font-size: var(--font-size-xs);
  color: var(--text-muted);
}

/* Proste kółko ładowania w przycisku */
.spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.6);
  border-top-color: var(--surface-primary);
  border-radius: 50%;
  margin-right: var(--spacing-sm);
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
